import { createTagFilterRule } from "../../../companies/tags/filter_rules";
// convex/lib/config/domains/invoices.ts

import { validatorInvoice } from "../../../companies/invoices";
import { validatorInvoicePayment } from "../../../companies/invoices/payments";
import { invoices_configurations } from "../../../companies/invoices/configuration";
import { validatorCancelationRequest } from "../../../companies/invoices/cancelation/requests";
import { invoiceExemptionValidator } from "../../../companies/invoices/exemptions";
import { validatorClientCredit } from "../../../companies/invoices/credits";
import { validatorInvoiceItem } from "../../../companies/invoices/items";
import { exemptInvoiceConfigurationValidator } from "../../../companies/invoices/configuration/exempts";
import { validatorClient } from "../../../companies/clients";
import { clientExemptDocumentValidator } from "../../../companies/clients/exempt_documents";
import { validatorSeller } from "../../../companies/sales/sellers";
import { saleValidator } from "../../../companies/sales";
import { validatorProductService } from "../../../companies/products";
import { validatorUsers } from "../../../system/users";
import { validatorBranch } from "../../../companies/branches";
import { invoiceStatusRule } from "../../../companies/invoices/filter_rules";
import type { DomainGroup, TableScope } from "../types";

// ============================================================================
// INVOICES DOMAIN - Status Values for i18n
// ============================================================================

export const INVOICE_STATUSES = [
  "draft",
  "issued",
  "partial",
  "paid",
  "cancelled",
  "overdue",
] as const;
export const PAYMENT_STATUSES = ["pending", "confirmed", "cancelled"] as const;
export const CREDIT_STATUSES = ["available", "used", "expired"] as const;
export const EXEMPT_DOCUMENT_TYPES = [
  "tax_exemption_certificate",
  "exempt_purchase_order",
  "diplomatic_exemption",
  "ngo_exemption",
  "other",
] as const;

export const invoicesDomain: DomainGroup = {
  id: "invoices",
  name: "Facturas",
  description: "Gestión de facturas, pagos y créditos",
  icon: "Receipt",
  order: 7,
  status: "active",

  // ── Configuración i18n del dominio ────────────────────
  i18n: {
    namespace: "invoices",
    keys: {
      title: "invoices.title",
      description: "invoices.description",
    },
  },

  modules: [
    {
      id: "invoices",
      name: "Facturas",
      dashboardAccess: "elevated",
      description: "Emisión y gestión de facturas",
      icon: "FileText",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/invoices"],
      navigation: {
        showInNav: true,
        contexts: ["main", "billing", "mobile"],
        navOrder: 1,
        status: "active",
      },

      // ── Configuración i18n del módulo ────────────────────
      i18n: {
        namespace: "invoices.invoices",
        keys: {
          title: "invoices.invoices.title",
          description: "invoices.invoices.description",
          longDescription: "invoices.invoices.longDescription",
          searchPlaceholder: "invoices.invoices.searchPlaceholder",
          emptyState: "invoices.invoices.emptyState",
          actions: {
            create: "invoices.invoices.actions.create",
            edit: "invoices.invoices.actions.edit",
            delete: "invoices.invoices.actions.delete",
            view: "invoices.invoices.actions.view",
          },
        },
        translatableFields: ["notes"],
        translatableEnums: [{ field: "status", values: INVOICE_STATUSES }],
      },

      tables: [
        {
          tableName: "invoices_items",
          schema: validatorInvoiceItem,
          scope: "company" as TableScope,
          relations: [
            {
              key: "invoice",
              schema: validatorInvoice,
              tableName: "invoices",
            },
            {
              key: "product",
              sourceField: "productId",
              schema: validatorProductService,
              tableName: "products",
            },
          ],
          searchFields: ["name", "description"],
          hasStatusIndex: false,
          customRules: [],
        },
        {
          tableName: "invoices",
          schema: validatorInvoice,
          scriptActions: ["complete", "process"],
          scope: "branch" as TableScope,
          relations: [
            {
              key: "client",
              schema: validatorClient,
              tableName: "clients",
              searchableFields: ["name", "contactPerson"],
            },
            {
              key: "seller",
              schema: validatorSeller,
              tableName: "sellers",
            },
            {
              key: "sale",
              schema: saleValidator,
              tableName: "sales",
            },
            {
              key: "payment",
              schema: validatorInvoicePayment,
              tableName: "invoices_payments",
            },
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "lastCreatedBy",
              sourceField: "internalControl.createdBy",
              schema: validatorUsers,
              tableName: "users",
            },
          ],
          searchFields: [
            "invoiceNumber",
            "reference",
            "notes",
            "correlative",
            "code",
          ],
          hasStatusIndex: false,
          customRules: [invoiceStatusRule, createTagFilterRule("invoices")],
          withTags: true,
        },
        {
          tableName: "invoices_payments",
          schema: validatorInvoicePayment,
          scope: "branch" as TableScope,
          relations: [
            {
              key: "invoice",
              schema: validatorInvoice,
              tableName: "invoices",
            },
            {
              key: "client",
              schema: validatorClient,
              tableName: "clients",
            },
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "lastCreatedBy",
              sourceField: "internalControl.createdBy",
              schema: validatorUsers,
              tableName: "users",
            },
          ],
          searchFields: ["paymentMethod", "status", "invoiceId"],
          hasStatusIndex: false,
          customRules: [],
        },
        {
          tableName: "invoices_exemptions",
          schema: invoiceExemptionValidator,
          scope: "branch" as TableScope,
          relations: [
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "lastCreatedBy",
              sourceField: "internalControl.createdBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "exemptDocumentsData",
              sourceField: "exemptDocuments",
              schema: clientExemptDocumentValidator,
              tableName: "clients_exempt_documents",
              many: true,
            },
            {
              key: "invoice",
              schema: validatorInvoice,
              tableName: "invoices",
            },
            {
              key: "client",
              schema: validatorClient,
              tableName: "clients",
            },
          ],
          searchFields: [],
          hasStatusIndex: false,
          customRules: [],
        },
        {
          tableName: "invoices_cancelation_requests",
          schema: validatorCancelationRequest,
          scope: "branch" as TableScope,
          relations: [
            {
              key: "invoice",
              schema: validatorInvoice,
              tableName: "invoices",
            },
            {
              key: "client",
              schema: validatorClient,
              tableName: "clients",
            },
            {
              key: "approvedByUser",
              schema: validatorUsers,
              tableName: "users",
              sourceField: "approvedBy",
            },
            {
              key: "rejectedByUser",
              schema: validatorUsers,
              tableName: "users",
              sourceField: "rejectedBy",
            },
            {
              key: "requestedByUser",
              schema: validatorUsers,
              tableName: "users",
              sourceField: "requestedBy",
            },
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "lastCreatedBy",
              sourceField: "internalControl.createdBy",
              schema: validatorUsers,
              tableName: "users",
            },
          ],
          searchFields: ["status"],
          hasStatusIndex: true,
          customRules: [],
        },
        {
          tableName: "client_credits",
          schema: validatorClientCredit,
          scope: "branch" as TableScope,
          relations: [
            {
              key: "client",
              schema: validatorClient,
              tableName: "clients",
              sourceField: "clientId",
            },
            {
              key: "invoice",
              schema: validatorInvoice,
              tableName: "invoices",
              sourceField: "invoiceId",
            },
            {
              key: "payment",
              schema: validatorInvoicePayment,
              tableName: "invoices_payments",
              sourceField: "paymentId",
            },
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "lastCreatedBy",
              sourceField: "internalControl.createdBy",
              schema: validatorUsers,
              tableName: "users",
            },
          ],
          searchFields: ["notes"],
          hasStatusIndex: true,
          customRules: [],
        },
      ],
      readOnlyTables: [
        { tableName: "invoices" },
        { tableName: "clients" },
        { tableName: "clients_exempt_documents" },
      ],
    },
    {
      id: "invoices_configurations",
      name: "Facturación",
      description: "Configuración de facturación, impresión y reglas fiscales",
      icon: "Settings2",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/settings/invoices"],
      navigation: {
        showInNav: true,
        contexts: ["billing", "settings"],
        navOrder: 4,
        status: "active",
      },

      i18n: {
        namespace: "invoices.configuration",
        keys: {
          title: "invoices.configuration.title",
          description: "invoices.configuration.description",
          longDescription: "invoices.configuration.longDescription",
          searchPlaceholder: "invoices.configuration.searchPlaceholder",
          emptyState: "invoices.configuration.emptyState",
          actions: {
            create: "invoices.configuration.actions.create",
            edit: "invoices.configuration.actions.edit",
            delete: "invoices.configuration.actions.delete",
            view: "invoices.configuration.actions.view",
          },
        },
        translatableFields: [],
        translatableEnums: [],
      },

      tables: [
        {
          tableName: "invoices_configurations",
          schema: invoices_configurations,
          scope: "branch" as TableScope,
          relations: [
            {
              key: "branch",
              sourceField: "branchId",
              schema: validatorBranch,
              tableName: "branches",
            },
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "lastCreatedBy",
              sourceField: "internalControl.createdBy",
              schema: validatorUsers,
              tableName: "users",
            },
          ],
          searchFields: [],
          hasStatusIndex: true,
          customRules: [],
        },
        {
          tableName: "invoices_configuration_exempts",
          schema: exemptInvoiceConfigurationValidator,
          scope: "branch" as TableScope,
          relations: [],
          searchFields: ["title", "type"],
          hasStatusIndex: false,
          customRules: [],
        },
      ],
      readOnlyTables: [{ tableName: "branches" }],
    },
    {
      id: "invoices_payments",
      name: "Pagos",
      description: "Registros de pagos y métodos de pago",
      icon: "CreditCard",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/invoices/payments"],
      navigation: {
        showInNav: true,
        contexts: ["main", "billing"],
        navOrder: { main: 2, billing: 3 },
        status: "active",
      },

      // ── Configuración i18n del módulo ────────────────────
      i18n: {
        namespace: "invoices.invoices.payments",
        keys: {
          title: "invoices.invoices.payments.title",
          description: "invoices.invoices.payments.description",
          longDescription: "invoices.invoices.payments.longDescription",
          searchPlaceholder: "invoices.invoices.payments.searchPlaceholder",
          emptyState: "invoices.invoices.payments.emptyState",
          actions: {
            create: "invoices.invoices.payments.actions.create",
            edit: "invoices.invoices.payments.actions.edit",
            delete: "invoices.invoices.payments.actions.delete",
            view: "invoices.invoices.payments.actions.view",
          },
        },
        translatableFields: [],
        translatableEnums: [{ field: "status", values: PAYMENT_STATUSES }],
      },

      tables: [
        {
          tableName: "invoices_payments",
          schema: validatorInvoicePayment,
          scope: "branch" as TableScope,
          relations: [
            {
              key: "invoice",
              schema: validatorInvoice,
              tableName: "invoices",
            },
            {
              key: "client",
              schema: validatorClient,
              tableName: "clients",
            },
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "lastCreatedBy",
              sourceField: "internalControl.createdBy",
              schema: validatorUsers,
              tableName: "users",
            },
          ],
          searchFields: ["paymentMethod", "status", "invoiceId"],
          hasStatusIndex: false,
          customRules: [],
        },
      ],
      readOnlyTables: [{ tableName: "invoices" }, { tableName: "clients" }],
    },
    {
      id: "client_credits",
      name: "Créditos",
      description: "Gestión de créditos y límites de crédito",
      icon: "Wallet",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/invoices/credits"],
      navigation: {
        showInNav: true,
        contexts: ["main", "billing"],
        navOrder: { main: 3, billing: 4 },
        status: "active",
      },

      // ── Configuración i18n del módulo ────────────────────
      i18n: {
        namespace: "invoices.client_credits",
        keys: {
          title: "invoices.client_credits.title",
          description: "invoices.client_credits.description",
          longDescription: "invoices.client_credits.longDescription",
          searchPlaceholder: "invoices.client_credits.searchPlaceholder",
          emptyState: "invoices.client_credits.emptyState",
          actions: {
            create: "invoices.client_credits.actions.create",
            edit: "invoices.client_credits.actions.edit",
            delete: "invoices.client_credits.actions.delete",
            view: "invoices.client_credits.actions.view",
          },
        },
        translatableFields: ["notes"],
        translatableEnums: [{ field: "status", values: CREDIT_STATUSES }],
      },

      tables: [
        {
          tableName: "client_credits",
          schema: validatorClientCredit,
          scope: "branch" as TableScope,
          relations: [
            {
              key: "client",
              schema: validatorClient,
              tableName: "clients",
              sourceField: "clientId",
            },
            {
              key: "invoice",
              schema: validatorInvoice,
              tableName: "invoices",
              sourceField: "invoiceId",
            },
            {
              key: "payment",
              schema: validatorInvoicePayment,
              tableName: "invoices_payments",
              sourceField: "paymentId",
            },
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "lastCreatedBy",
              sourceField: "internalControl.createdBy",
              schema: validatorUsers,
              tableName: "users",
            },
          ],
          searchFields: ["notes"],
          hasStatusIndex: true,
          customRules: [],
        },
      ],
      readOnlyTables: [
        { tableName: "invoices" },
        { tableName: "clients" },
        { tableName: "invoices_payments" },
      ],
    },
    {
      id: "invoices_exemptions",
      name: "Exoneraciones",
      description: "Gestión de exenciones fiscales y certificados",
      icon: "FileCheck",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/invoices/exemptions"],
      navigation: {
        showInNav: true,
        contexts: ["main", "billing"],
        navOrder: { main: 4, billing: 5 },
        status: "active",
      },

      // ── Configuración i18n del módulo ────────────────────
      i18n: {
        namespace: "invoices.exemptions",
        keys: {
          title: "invoices.exemptions.title",
          description: "invoices.exemptions.description",
          longDescription: "invoices.exemptions.longDescription",
          searchPlaceholder: "invoices.exemptions.searchPlaceholder",
          emptyState: "invoices.exemptions.emptyState",
          actions: {
            create: "invoices.exemptions.actions.create",
            edit: "invoices.exemptions.actions.edit",
            delete: "invoices.exemptions.actions.delete",
            view: "invoices.exemptions.actions.view",
          },
        },
        translatableFields: [],
        translatableEnums: [{ field: "type", values: EXEMPT_DOCUMENT_TYPES }],
      },

      tables: [
        {
          tableName: "invoices_exemptions",
          schema: invoiceExemptionValidator,
          scope: "branch" as TableScope,
          relations: [
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "lastCreatedBy",
              sourceField: "internalControl.createdBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "exemptDocumentsData",
              sourceField: "exemptDocuments",
              schema: clientExemptDocumentValidator,
              tableName: "clients_exempt_documents",
              many: true,
            },
            {
              key: "invoice",
              schema: validatorInvoice,
              tableName: "invoices",
            },
            {
              key: "client",
              schema: validatorClient,
              tableName: "clients",
            },
          ],
          searchFields: [],
          hasStatusIndex: false,
          customRules: [],
        },
      ],
      readOnlyTables: [
        { tableName: "invoices" },
        { tableName: "clients" },
        { tableName: "clients_exempt_documents" },
      ],
    },
    {
      id: "invoices_cancelation_requests",
      name: "Anulaciones",
      description: "Solicitudes y gestión de anulaciones de facturas",
      icon: "XCircle",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/invoices/cancelations"],
      navigation: {
        showInNav: true,
        contexts: ["main", "billing"],
        navOrder: { main: 5, billing: 6 },
        status: "active",
      },

      // ── Configuración i18n del módulo ────────────────────
      i18n: {
        namespace: "invoices.cancelation_requests",
        keys: {
          title: "invoices.cancelation_requests.title",
          description: "invoices.cancelation_requests.description",
          longDescription: "invoices.cancelation_requests.longDescription",
          searchPlaceholder: "invoices.cancelation_requests.searchPlaceholder",
          emptyState: "invoices.cancelation_requests.emptyState",
          actions: {
            create: "invoices.cancelation_requests.actions.create",
            edit: "invoices.cancelation_requests.actions.edit",
            delete: "invoices.cancelation_requests.actions.delete",
            view: "invoices.cancelation_requests.actions.view",
          },
        },
        translatableFields: [],
        translatableEnums: [],
      },

      tables: [
        {
          tableName: "invoices_cancelation_requests",
          schema: validatorCancelationRequest,
          scope: "branch" as TableScope,
          relations: [
            {
              key: "invoice",
              schema: validatorInvoice,
              tableName: "invoices",
            },
            {
              key: "client",
              schema: validatorClient,
              tableName: "clients",
            },
            {
              key: "approvedByUser",
              schema: validatorUsers,
              tableName: "users",
              sourceField: "approvedBy",
            },
            {
              key: "rejectedByUser",
              schema: validatorUsers,
              tableName: "users",
              sourceField: "rejectedBy",
            },
            {
              key: "requestedByUser",
              schema: validatorUsers,
              tableName: "users",
              sourceField: "requestedBy",
            },
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "lastCreatedBy",
              sourceField: "internalControl.createdBy",
              schema: validatorUsers,
              tableName: "users",
            },
          ],
          searchFields: ["status"],
          hasStatusIndex: true,
          customRules: [],
        },
      ],
      readOnlyTables: [{ tableName: "invoices" }, { tableName: "clients" }],
    },
  ],
};
